<script lang="ts">
  import { route } from "@mateothegreat/svelte5-router";
  import { ArrowLeft } from "lucide-svelte";
  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  import { BLOG_POSTS, type BlogPost } from "./posts";
  import "../../../highlight";

  const slug = $derived(window.location.pathname.replace("/learn/", ""));

  const post: BlogPost | undefined = $derived(
    BLOG_POSTS.find((p) => p.slug === slug)
  );

  $effect(() => {
    if (post) {
      updateMeta({
        title: `${post.title} | 0xfire`,
        description: post.description,
        ogTitle: post.title,
        ogDescription: post.description,
        ogType: "article",
        ogImage: "https://0xfire.com/0xfire.png",
        canonical: `https://0xfire.com/learn/${post.slug}`,
        keywords: post.keywords,
      });
      injectJsonLd(combineSchemas(
        ORGANIZATION_SCHEMA,
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          datePublished: post.date,
          author: { "@type": "Organization", name: "0xfire" },
          publisher: { "@type": "Organization", name: "0xfire", url: "https://0xfire.com" },
        },
        getBreadcrumbSchema([
          { name: "Home", url: "https://0xfire.com/" },
          { name: "Learn", url: "https://0xfire.com/learn" },
          { name: post.title, url: `https://0xfire.com/learn/${post.slug}` },
        ]),
      ));
    }
  });
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-orange-900 selection:text-white">
  <div class="max-w-3xl mx-auto px-4 py-12">
    <a use:route href="/learn" class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors mb-8">
      <ArrowLeft class="size-4" /> All tutorials
    </a>

    {#if post}
      <article class="prose prose-sm prose-invert max-w-none prose-headings:tracking-tight prose-a:text-orange-500 prose-code:text-gray-400 prose-code:bg-gray-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-950 prose-pre:border prose-pre:border-gray-800">
        <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest !mb-4">{post.date}</p>
        {@html post.html}
      </article>

      <!-- Related Links -->
      <div class="border-t border-gray-800 mt-16 pt-8">
        <p class="text-xs font-mono text-gray-600 uppercase tracking-widest mb-4">Related</p>
        <div class="flex flex-wrap gap-3">
          <a use:route href="/products/sailfish" class="text-sm text-gray-500 hover:text-white transition-colors">Sailfish API</a>
          <span class="text-gray-700">&middot;</span>
          <a use:route href="/tools" class="text-sm text-gray-500 hover:text-white transition-colors">Free Tools</a>
          <span class="text-gray-700">&middot;</span>
          <a use:route href="/services" class="text-sm text-gray-500 hover:text-white transition-colors">Services</a>
          <span class="text-gray-700">&middot;</span>
          <a use:route href="/learn" class="text-sm text-gray-500 hover:text-white transition-colors">All Articles</a>
        </div>
      </div>
    {:else}
      <div class="text-center py-20">
        <p class="text-2xl font-bold text-white mb-4">Post not found</p>
        <a use:route href="/learn" class="text-orange-500 hover:underline">All tutorials</a>
      </div>
    {/if}
  </div>
</div>
